import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, Loader2, Plus, Tags, X } from "lucide-react";
import { adminApi, formatApiError } from "@/context/AuthContext";
import { LIEUX } from "@/components/site/nav";

const LABEL = Object.fromEntries(LIEUX.map((l) => [l.key, l.label]));
const KEY = ["admin-place-suggestions"];
const btn = "inline-flex items-center gap-1.5 uppercase text-[11px] font-bold tracking-[0.12em] px-3 h-9 transition-colors disabled:opacity-50";

const Row = ({ s, onDone }) => {
  const [busy, setBusy] = useState(false);
  const act = async (path, body) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/places/${s.id}/${path}`, body);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <li className="flex flex-wrap items-center gap-3 py-3 border-b border-white/10" data-testid={`suggest-row-${s.id}-${s.suggest}`}>
      <div className="flex-1 min-w-[180px]">
        <p className="text-white font-semibold">{s.name}</p>
        <p className="text-zinc-400 text-xs">{s.city} · aujourd'hui : {s.categories.map((c) => LABEL[c] || c).join(", ")}</p>
      </div>
      <button type="button" disabled={busy} onClick={() => act("add-category", { category: s.suggest })} data-testid={`suggest-apply-${s.id}-${s.suggest}`}
        className={`${btn} bg-primary text-white hover:bg-white hover:text-black`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter à {LABEL[s.suggest]}
      </button>
      <button type="button" disabled={busy} onClick={() => act("dismiss-suggestion")} data-testid={`suggest-dismiss-${s.id}-${s.suggest}`}
        className={`${btn} border border-white/25 text-zinc-200 hover:border-white`}>
        <X className="w-4 h-4" /> Ignorer
      </button>
    </li>
  );
};

// « À reclasser » : places whose name says café / galerie / librairie, one click to file them there
export const PlaceSuggestions = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const { data } = useQuery({ queryKey: KEY, queryFn: async () => (await adminApi.get("/admin/places/suggestions")).data });
  const list = Array.isArray(data) ? data : [];
  if (!list.length) return null;
  const onDone = () => {
    qc.invalidateQueries({ queryKey: KEY });
    qc.invalidateQueries({ queryKey: ["/places"] });
  };
  return (
    <section className="border border-white/25 bg-white/[0.04] p-4 mb-8" data-testid="place-suggestions">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="place-suggestions-toggle" className="w-full flex items-center gap-3 text-left">
        <Tags className="w-5 h-5 text-primary" />
        <span className="font-display uppercase text-xl text-white flex-1">À reclasser · <span data-testid="place-suggestions-count">{list.length}</span> suggestion{list.length > 1 ? "s" : ""}</span>
        <ChevronDown className={`w-5 h-5 text-white transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <p className="text-zinc-300 text-sm mt-2">Ces lieux ont « café », « galerie » ou « librairie » dans leur nom : un clic les ajoute à la catégorie (les autres catégories sont gardées).</p>
          <ul className="mt-3 max-h-[60vh] overflow-y-auto pr-1" data-testid="place-suggestions-list">
            {list.map((s) => <Row key={`${s.id}-${s.suggest}`} s={s} onDone={onDone} />)}
          </ul>
        </>
      )}
    </section>
  );
};
